/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, ObservableMap, flow } from 'mobx';
import { generateList, generateListRefresh, generateListMore, isError } from './actionCreator';
import ImageCompressor from 'image-compressor.js';
import fetch from '../fetch';
import FormData from 'form-data';
import forEach from 'lodash/forEach';
import ErrorsStore from './errorsStore';
import get from 'lodash/get';
import set from 'lodash/set';
import tasksStore from './tasksStore';
import _ from '../translations';

class ImagesStore {
    @observable
    collection = new ObservableMap();
    @observable
    details = new ObservableMap();

    constructor() {
        this.baseUrl = '/api/assets/images';
    }

    @action
    reset = () => {
        this.collection.clear();
        this.details.clear();
    };

    @action.bound
    detail = flow(function*({ _id = false } = {}) {});

    @action.bound
    list = flow(function*({ hash, params } = {}) {
        return yield generateList({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing images'),
        })();
    });

    @action.bound
    listRefresh = flow(function*({ hash, params = {}, maxPagesToRefresh } = {}) {
        return yield generateListRefresh({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing images'),
            maxPagesToRefresh,
        })();
    });

    @action.bound
    listMore = flow(function*({ hash, params = {} } = {}) {
        return yield generateListMore({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing images'),
        })();
    });

    @action.bound
    checkImage = flow(function*(_id) {
        if (!_id) return;
        try {
            const check = yield fetch.get({
                url: `/api/assets/images/check/${_id}`,
                params: {},
            });
            if (isError(check)) {
                console.log('check', check);
                //ErrorsStore.addError(check, '');
            }
            return check;
        } catch (e) {
            console.warn(e);
            // ErrorsStore.addError(e, 'There was a problem getting the image');
            return;
        }
    });

    @action.bound
    create = flow(function*({ body, onProgress } = {}) {
        if (!body) return;

        let file;
        try {
            file = yield this.compressImage(get(body, 'original.source'));
            set(body, 'original.source', file);
        } catch (e) {
            console.warn(e);
            ErrorsStore.addError(e, _('There was a problem compressing the image'));
            return;
        }

        const form = this.buildForm({
            data: {
                image: {
                    ...body,
                },
            },
        });

        this.progress = 0;
        let response = {};
        try {
            response = yield fetch.submit({
                form,
                url: '/api/assets/images/create',
                onProgress: percent => {
                    if (onProgress) onProgress(percent);
                },
            });
            tasksStore.markAsDone(6);
        } catch (e) {
            ErrorsStore.addError(e, _('There was a problem creating the image'));
            return;
        }

        if (isError(response)) {
            ErrorsStore.addError(
                {
                    message: _('There was an error creating the image'),
                },
                _('Nunchee services are down right now, try again later')
            );
            return;
        }
        return get(response, 'body.data', false);
    });

    @action.bound
    update({ item = {} } = {}) {}

    @action.bound
    delete = flow(function*({ _id = false, type = 'logical' } = {}) {
        try {
            const response = yield fetch.delete({
                url: `${this.baseUrl}/singleDelete/${_id}`,
                body: {},
            });
            if (response && !isError(response) && this.details.has(_id)) this.details.delete(_id);
            this.collection.forEach((value, key, map) => {
                if (value.items) {
                    value.items = value.items.filter(item => item._id !== _id);
                }
            });
            return response;
        } catch (e) {
            ErrorsStore.addError(e, _('There was a problem deleting the image'));
            console.warn(e);
            return false;
        }
    });

    buildForm = formObj => {
        let file = get(formObj, 'data.image.original.source');
        delete formObj.data.image.original.source;
        formObj = this.flattenObject(formObj);
        const form = new FormData();
        forEach(formObj, (field, key) => {
            form.append(key, field);
        });
        form.append('data.image.original.source', file);
        return form;
    };

    compressImage = file => {
        return new Promise((resolve, reject) => {
            new ImageCompressor(file, {
                quality: 1,
                success(result) {
                    resolve(result);
                },
                error(e) {
                    reject(e);
                },
            });
        });
    };

    flattenObject = ob => {
        let toReturn = {};

        for (let i in ob) {
            if (!ob.hasOwnProperty(i)) continue;

            if (typeof ob[i] === 'object') {
                let flatObject = this.flattenObject(ob[i]);
                for (let x in flatObject) {
                    if (!flatObject.hasOwnProperty(x)) continue;
                    toReturn[i + '.' + x] = flatObject[x];
                }
            } else {
                toReturn[i] = ob[i];
            }
        }
        return toReturn;
    };
}

export default new ImagesStore();
